import { assertNever } from "@@/shared/assert-never";
import type { OrderGroup } from "@@/shared/order-groups/order-group";
import { paymentProvider } from "@@/shared/payments/payment-providers";
import { priceFactory, zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { systemUser } from "@@/shared/users/system-user";
import { isUserId } from "@@/shared/users/user-id";
import type { UUID } from "@@/shared/uuid";
import { asArray } from "@@/utilities/as-array";
import { generateId } from "@@/utilities/generate-id";
import type { OrderV1 } from "../order";
import type { OrderStatus } from "../order-state";
import type { OrderEvent } from "./events/_order-event";
import { orderEventBookableReservationUpdatedFactory } from "./events/order-bookable-table-reservation-updated";
import { orderCancelledEventFactory } from "./events/order-cancelled.event";
import { orderEventConfirmedFactory } from "./events/order-confirmed.event";
import type {
    OrderCreatedEvent} from "./events/order-created.event";
import {
    orderCreatedEventFactory,
} from "./events/order-created.event";
import { orderFulfilledEventFactory } from "./events/order-fulfilled.event";
import { orderFullRefundRequestedEventFactory } from "./events/order-full-refund-requested.event";
import { orderItemBookableTimeUpdatedEventFactory } from "./events/order-item-events/order-item-bookable-time-updated.event";
import { orderEventOutForDeliveryFactory } from "./events/order-out-for-delivery.event";
import { orderPartialRefundRequestedEventFactory } from "./events/order-partial-refund-requested.event";
import { orderEventPaymentAmountCapturableUpdatedFactory } from "./events/order-payment-events/order-payment-amount-capturable-updated.event";
import { orderEventPaymentFailedFactory } from "./events/order-payment-events/order-payment-failed.event";
import { orderPaymentFulfilledEventFactory } from "./events/order-payment-events/order-payment-fulfilled.event";
import { orderEventPendingPaymentOnPremiseFactory } from "./events/order-payment-events/order-payment-pending.event";
import { orderPaymentRefundFulfilledEventFactory } from "./events/order-payment-events/order-payment-refund-fulfilled.event";
import type {
    OrderPaymentRefundInitiatedEvent} from "./events/order-payment-events/order-payment-refund-initiated.event";
import {
    orderEventPaymentRefundInitiatedFactory,
} from "./events/order-payment-events/order-payment-refund-initiated.event";
import { orderEventPaymentRefundPendingFactory } from "./events/order-payment-events/order-payment-refund-pending.event";
import { orderEventPendingPaymentConfirmationFactory } from "./events/order-payment-events/order-pending-payment-confirmation.event";
import { orderPendingConfirmationEventFactory } from "./events/order-pending-confirmation.event";
import { orderEventPendingDeliveryFactory } from "./events/order-pending-delivery.event";
import { orderEventReadyForPickUpFactory } from "./events/order-ready-for-pickup.event";
import { orderEventRejectedFactory } from "./events/order-rejected.event";

const convertOrderStatusToOrderEvents = (params: {
    readonly orderCreatedEvent: OrderCreatedEvent;
    readonly order: OrderV1;
    readonly orderGroup: OrderGroup;
    readonly status: OrderStatus;
    readonly _version: number;
    readonly alreadyEvents: OrderEvent[];
}): OrderEvent[] => {
    const {
        orderGroup,
        orderCreatedEvent,
        order,
        status,
        _version,
        alreadyEvents,
    } = params;
    const result = _convertOrderStatusToOrderEvents(
        orderCreatedEvent,
        order,
        orderGroup,
        status,
        _version,
        alreadyEvents,
    );

    const events = asArray(result);
    //To keep dates consistent, we use the createdAt date from the status
    return events.map(event => ({
        ...event,
        _createdAt: status._createdAt,
        _type: "ORDER_EVENT",
        orderId: order._id,
        orderGroupId: order.orderGroupId,
        providerId: order.providerId,
        providerSlug: order.providerSlug ?? "",
    }));
};

const _convertOrderStatusToOrderEvents = (
    orderCreatedEvent: OrderCreatedEvent,
    order: OrderV1,
    orderGroup: OrderGroup,
    status: OrderStatus,
    _version: number,
    alreadyEvents: OrderEvent[],
): OrderEvent | OrderEvent[] => {
    switch (status.type) {
        case "BOOKABLE_TABLE_RESERVATION_UPDATED":
            return orderEventBookableReservationUpdatedFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "CONFIRMED": {
            return orderEventConfirmedFactory({
                ...status,
                _version,
                confirmedBy: status.confirmedBy,
                estimatedDeliveryTime: order.delivery.estimatedDeliveryTime,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        }
        case "BOOKABLE_TIME_CHANGE":
            return orderItemBookableTimeUpdatedEventFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "BOOKABLE_TIME_UPDATED":
            return orderItemBookableTimeUpdatedEventFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "CANCELLED":
            return orderCancelledEventFactory({
                ...status,
                _version,
                cancelledBy: status.cancelledBy,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "CREATED": {
            if (order._type !== "ORDER")
                throw new Error(
                    `Invalid order type, cannot create an order event from an "${order.type}", ${order._id}`,
                );
            // Why not use status.orderId? Because it is not always the same as the actual orderId. Taste on that sour-caramel!
            return orderCreatedEventFactory({
                ...status,
                order,
                orderGroupHasLegacyServiceFee: !!orderGroup.customerServiceFee,
            });
        }
        case "FULFILLED":
            return orderFulfilledEventFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "OUT_FOR_DELIVERY":
            return orderEventOutForDeliveryFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "PAYMENT_FAILED":
            return orderEventPaymentFailedFactory({
                ...status,
                _version,
                paymentFor: {
                    orderEventId: orderCreatedEvent._id,
                },
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "REJECTED":
            return orderEventRejectedFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "READY_FOR_PICK_UP":
            return orderEventReadyForPickUpFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "PENDING_CONFIRMATION": {
            // TODO: @emanuel @jakob make sure this is correct:
            const hasStripePayment = order.statusHistory.some(
                item =>
                    item.type === "PAYMENT_PENDING" &&
                    item.paymentProvider === "STRIPE",
            );
            const orderEventPendingConfirmation =
                orderPendingConfirmationEventFactory({
                    ...status,
                    _version,
                    estimatedDeliveryTime: order.delivery.estimatedDeliveryTime,
                    providerId: order.providerId,
                    providerSlug: order.providerSlug ?? "",
                });

            if (!hasStripePayment) {
                return orderEventPendingConfirmation;
            }
            return [
                orderEventPaymentAmountCapturableUpdatedFactory({
                    _version,
                    amountCapturable: order.totalPrice.amountIncludingVat,
                    paymentProvider: paymentProvider.STRIPE,
                    createdBy: paymentProvider.STRIPE,
                    paymentFor: {
                        orderEventId: orderCreatedEvent._id,
                    },
                    orderGroupId: order.orderGroupId,
                    orderId: order._id,
                    providerId: order.providerId,
                    providerSlug: order.providerSlug,
                }),
                {
                    ...orderEventPendingConfirmation,
                    _version: _version + 1,
                },
            ];
        }
        case "PENDING_PAYMENT_CONFIRMATION":
            return orderEventPendingPaymentConfirmationFactory({
                ...status,
                _version,
                paymentFor: {
                    orderEventId: orderCreatedEvent._id,
                },
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "PENDING_DELIVERY":
            return orderEventPendingDeliveryFactory({
                ...status,
                _version,
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
            });
        case "PAYMENT_PENDING": {
            // This is a legacy status
            // and it was only used for on premise payments
            // when user had chosen for customer to pay later
            switch (status.paymentProvider) {
                case "PAY_REQUEST":
                    throw new Error(
                        "PAY_REQUEST should never be converted, it was built after con_version was necessary",
                    );
                default: {
                    return orderEventPendingPaymentOnPremiseFactory({
                        orderGroupId: status.orderGroupId,
                        orderId: status.orderId,
                        paymentProvider: status.paymentProvider,
                        paymentFor: {
                            orderEventId: orderCreatedEvent._id,
                        },
                        _version,
                        providerId: order.providerId,
                        providerSlug: order.providerSlug ?? "",
                    });
                }
            }
        }
        case "PAYMENT_FULFILLED": {
            const paidAmount =
                status.paidAmount === order.totalPrice.amountIncludingVat
                    ? sumPrices([
                          order.totalPrice,
                          orderGroup.customerServiceFee ?? zeroPrice,
                      ])
                    : priceFactory({ amountIncludingVat: status.paidAmount });

            return orderPaymentFulfilledEventFactory({
                ...status,
                _version,
                paymentFor: {
                    orderEventId: orderCreatedEvent._id,
                },
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
                // We didn't count the service fee in the paid amount
                // to now show it to the provider earlier, but we do
                // need to add it to the new event to keep the full
                // correct data in the event
                paidAmount: paidAmount,
                // status.paidAmount +
                // (status.paidAmount === order.totalPrice.amountIncludingVat
                //     ? orderGroup.customerServiceFee?.amountIncludingVat ?? 0
                //     : 0),
                // we'll also add it as it's own property
                // to be able to check whether it was part of the payment
                legacyServiceFee:
                    orderGroup.customerServiceFee?.amountIncludingVat > 0,
            });
        }
        case "PAYMENT_REFUND_PENDING": {
            const refundInitiatedEvent = alreadyEvents.find(
                s =>
                    s.type === "PAYMENT_REFUND_INITIATED" &&
                    s.refundUUID === status.refundUUID,
            ) as OrderPaymentRefundInitiatedEvent | undefined;

            // We don't know if the payment amount here contains
            // the service fee or not, so we'll check in another event
            return [
                orderEventPaymentRefundPendingFactory({
                    ...status,
                    _version,
                    refundUUID: status.refundUUID as UUID,
                    paymentFor: {
                        orderEventId: orderCreatedEvent._id,
                    },
                    providerId: order.providerId,
                    providerSlug: order.providerSlug ?? "",
                    requestedRefund:
                        refundInitiatedEvent?.requestedRefund ??
                        priceFactory({
                            amountIncludingVat: status.requestedRefundAmount,
                        }),
                    percentage: refundInitiatedEvent?.percentage,
                }),
            ];
        }
        case "PAYMENT_REFUND_FAILED":
            // We don't know if the payment amount here contains
            // the service fee or not, so we'll check in another event
            throw new Error("Not implemented anymore missing payment provider");
        // return orderEventPaymentRefundFailedFactory({
        //     ...status,
        //     _version,
        //     paymentFor: {
        //         orderEventId: orderCreatedEvent._id,
        //     },
        //     paymentProvider: "STRIPE", // TODO!
        //     providerId: order.providerId,
        //     providerSlug: order.providerSlug ?? "",
        //     legacyServiceFee:
        //         orderGroup.customerServiceFee?.amountIncludingVat > 0,
        // });
        case "PAYMENT_REFUND_FULFILLED": {
            // Does the refund include a service fee?
            const hasServiceFeeCheck = (): boolean => {
                // if the order group doesn't have a service fee, then the refund doesn't either
                if (!orderGroup.customerServiceFee) return false;
                if (!orderGroup.customerServiceFee.amountIncludingVat)
                    return false;

                // requestedRefundAmount is not the same as refundedAmount
                // then requestedRefundAmount probably contains the service fee
                // if they are the same, no service fee
                if (status.refundedAmount === status.requestedRefundAmount)
                    return false;
                if (status.requestedRefundAmount - status.refundedAmount > 0)
                    return true;

                // fallback to false, or true.. I don't know
                return false;
            };
            const hasServiceFee = hasServiceFeeCheck();

            const hasStatus = order.statusHistory.find(
                s => s.type === "PAYMENT_REFUND_INITIATED",
            );
            if (status.refundUUID === undefined || !hasStatus) {
                const refundUUID =
                    status.refundUUID ??
                    (generateId("refund__") as unknown as UUID);
                const fullRefund =
                    status.refundedAmount ===
                    order.totalPrice.amountIncludingVat;
                const fullRefundPrice = sumPrices([
                    order.totalPrice,
                    hasServiceFee ? orderGroup.customerServiceFee : zeroPrice,
                ]);
                const startingEvent = fullRefund
                    ? orderFullRefundRequestedEventFactory({
                          _version,
                          amount: fullRefundPrice,
                          orderGroupId: order.orderGroupId,
                          orderId: order._id,
                          providerId: order.providerId,
                          providerSlug: order.providerSlug ?? "",
                          reason: status.reason ?? "",
                          requestCreatedBy: isUserId(status.createdBy)
                              ? status.createdBy
                              : systemUser,
                          legacyServiceFee: hasServiceFee,
                      })
                    : orderPartialRefundRequestedEventFactory({
                          _version,
                          amount: priceFactory({
                              amountIncludingVat: status.requestedRefundAmount,
                          }),
                          orderGroupId: order.orderGroupId,
                          orderId: order._id,
                          providerId: order.providerId,
                          providerSlug: order.providerSlug ?? "",
                          reason: status.reason ?? "",
                          requestCreatedBy: isUserId(status.createdBy)
                              ? status.createdBy
                              : systemUser,
                      });
                const requestedRefund = fullRefund
                    ? fullRefundPrice
                    : priceFactory({
                          amountIncludingVat: status.refundedAmount,
                      });
                return [
                    startingEvent,
                    orderEventPaymentRefundInitiatedFactory({
                        // ...status,
                        _version: _version + 1,
                        refundUUID: refundUUID as UUID,
                        requestedRefund: requestedRefund,
                        paymentFor: {
                            orderEventId: startingEvent._id,
                        },
                        orderGroupId: order.orderGroupId,
                        orderId: order._id,
                        providerId: order.providerId,
                        providerSlug: order.providerSlug ?? "",
                        createdBy: status.createdBy,
                        reason: status.reason ?? "",
                        paymentUUID: status.paymentUUID,
                        paymentProvider: "STRIPE",

                        legacyServiceFee: hasServiceFee,
                        refundFee:
                            status.refundedAmount >
                            order.totalPrice.amountIncludingVat,
                    }),
                    orderPaymentRefundFulfilledEventFactory({
                        ...status,
                        _version: _version + 2,
                        refundUUID: refundUUID as UUID,
                        //refundedAmount:startingEvent.re
                        paymentFor: {
                            orderEventId: startingEvent._id,
                        },
                        providerId: order.providerId,
                        providerSlug: order.providerSlug ?? "",
                        legacyServiceFee: hasServiceFee,
                        refundedAmount: requestedRefund,
                    }),
                ];
            }
            const refundInitiatedEvent = alreadyEvents.find(
                s =>
                    s.type === "PAYMENT_REFUND_INITIATED" &&
                    s.refundUUID === status.refundUUID,
            ) as OrderPaymentRefundInitiatedEvent | undefined;

            return orderPaymentRefundFulfilledEventFactory({
                ...status,
                _version,
                refundUUID: status.refundUUID as UUID,
                paymentFor: {
                    orderEventId: refundInitiatedEvent
                        ? refundInitiatedEvent.paymentFor.orderEventId
                        : orderCreatedEvent._id,
                },
                providerId: order.providerId,
                providerSlug: order.providerSlug ?? "",
                legacyServiceFee: hasServiceFee,
                refundedAmount:
                    refundInitiatedEvent?.requestedRefund ??
                    priceFactory({ amountIncludingVat: status.refundedAmount }),
                percentage: refundInitiatedEvent?.percentage,
            });
        }
        case "PAYMENT_REFUND_INITIATED": {
            // Does the refund include a service fee?
            const hasServiceFeeCheck = (): boolean => {
                // if the order group doesn't have a service fee, then the refund doesn't either
                if (!orderGroup.customerServiceFee) return false;
                if (!orderGroup.customerServiceFee.amountIncludingVat)
                    return false;

                // If status.requestedRefund is "ORDER", then the refund includes the service fee
                if (status.requestedRefund === "ORDER") return true;

                // if status refundFee is true, then the refund includes the service fee
                if (status.refundFee) return true;

                // if status.requestedRefund.amountIncludingVat is the same as the order total, then the refund includes the service fee
                if (
                    status.requestedRefund.amountIncludingVat ===
                    order.totalPrice.amountIncludingVat
                )
                    return true;

                // fallback to false, or true.. I don't know
                return false;
            };
            const hasServiceFee = hasServiceFeeCheck();
            const fullRefund =
                status.requestedRefund === "ORDER" ||
                order.totalPrice.amountIncludingVat ===
                    status.requestedRefund.amountIncludingVat;
            const fullRefundPrice = sumPrices([
                order.totalPrice,
                hasServiceFee ? orderGroup.customerServiceFee : zeroPrice,
            ]);
            const startingEvent = fullRefund
                ? orderFullRefundRequestedEventFactory({
                      _version,
                      amount: fullRefundPrice,
                      orderGroupId: order.orderGroupId,
                      orderId: order._id,
                      providerId: order.providerId,
                      providerSlug: order.providerSlug ?? "",
                      reason: status.reason,
                      requestCreatedBy: isUserId(status.createdBy)
                          ? status.createdBy
                          : systemUser,
                      legacyServiceFee: hasServiceFee,
                  })
                : orderPartialRefundRequestedEventFactory({
                      _version,
                      amount:
                          status.requestedRefund === "ORDER"
                              ? fullRefundPrice
                              : status.requestedRefund,
                      orderGroupId: order.orderGroupId,
                      orderId: order._id,
                      providerId: order.providerId,
                      providerSlug: order.providerSlug ?? "",
                      reason: status.reason,
                      requestCreatedBy: isUserId(status.createdBy)
                          ? status.createdBy
                          : systemUser,
                  });

            return [
                startingEvent,
                orderEventPaymentRefundInitiatedFactory({
                    ...status,
                    _version: _version + 1,
                    refundUUID: status.refundUUID as UUID,
                    requestedRefund:
                        status.requestedRefund === "ORDER" ||
                        status.requestedRefund.amountIncludingVat ===
                            order.totalPrice.amountIncludingVat
                            ? fullRefundPrice
                            : status.requestedRefund,
                    refundFee:
                        status.refundFee !== undefined
                            ? status.refundFee
                            : hasServiceFee &&
                              (status.requestedRefund === "ORDER" ||
                                  status.requestedRefund.amountIncludingVat ===
                                      order.totalPrice.amountIncludingVat),
                    paymentFor: {
                        orderEventId: startingEvent._id,
                    },
                    providerId: order.providerId,
                    providerSlug: order.providerSlug ?? "",
                    legacyServiceFee: hasServiceFee,
                }),
            ];
        }
        default:
            assertNever(status);
            throw new Error(
                "Unhandled order status; " + JSON.stringify(status?.type),
            );
    }
};

export { convertOrderStatusToOrderEvents };
